/**
 * Workbench draft (工作台草稿, `jerry_draft_docs`) types — 对话式起草工作台.
 *
 * Distinct entity from the one-shot drafting records in ./draft.ts
 * (`jerry_drafts` — immutable single-run generation results): a workbench
 * draft is a持续多轮编辑的文书实体 with optional matter attachment.
 * Field shapes mirror backend DraftDocResponse / DraftDocCreate /
 * DraftDocUpdate (app/modules/jerry/schemas.py); API layer camel-cases.
 *
 * Design: docs/iterations/draft-workbench-design.md
 * Data model: docs/models/draft-entity-model.md
 */

import type { DraftDocType, DraftStatus } from "./draft";
import type { RetrievalScope } from "./index";

/**
 * Workbench-draft lifecycle (DraftDocStatus) — round-trippable document
 * states, NOT the one-shot terminal statuses of DraftStatus.
 */
export type DraftDocStatus = "active" | "idle" | "completed" | "archived";

/** Chinese display labels for workbench-draft statuses. */
export const DRAFT_DOC_STATUS_LABELS: Record<DraftDocStatus, string> = {
  active: "编辑中",
  idle: "闲置",
  completed: "已完成",
  archived: "已归档",
};

/**
 * 单条锚点记录（D7 段落稳定锚点，阶段二）——镜像后端 `DraftDocAnchorEntry` /
 * `DraftDocAnchorResponse`（camelCase）。`anchorId` 是跨轮次稳定的段落
 * 标识；`blockHash` 是该块规范化文本的稳定哈希；`ordinal` 是同哈希块在
 * 正文中的出现序号（1 起算，区分正文中重复文案的关键）。生成与重挂算法见
 * `components/draft/editor/draftAnchors.ts`。
 */
export interface DraftDocAnchor {
  anchorId: string;
  blockHash: string;
  ordinal: number;
}

/** A workbench draft as returned by /draft-docs endpoints. */
export interface DraftDoc {
  id: string;
  /** null = 无事项自由起草（仅创建者可见） */
  matterId: string | null;
  createdBy: string;
  title: string;
  docType: DraftDocType | null;
  /** 起草会话引用；首轮 AI 对话建立前为 null（阶段 2 起有值） */
  lobsterSessionId: string | null;
  status: DraftDocStatus;
  /** 当前正文（markdown）；新建未成稿时为 null */
  currentBody: string | null;
  /** D7 段落稳定锚点数组（阶段二），与 currentBody 的块序列一一对应；
   * 从未分配过时为 null（存量草稿、或从未随正文上报锚点）。 */
  anchors: DraftDocAnchor[] | null;
  isNonDeliverable: boolean;
  savedDocumentId: string | null;
  /** 一键成稿来源（指向一次性生成记录）；直接对话起草为 null */
  sourceOneshotDraftId: string | null;
  /** 一键成稿来源记录的当前状态（起草解耦一期 T4）——生成中/失败态回查入口，
   * 与 sourceOneshotDraftId 是否为 null 一一对应；无来源时恒 null。 */
  oneshotStatus: DraftStatus | null;
  /** 一键成稿失败原因；仅 oneshotStatus==="failed" 时非 null。 */
  oneshotFailureReason: string | null;
  /** 审查「插入修改」来源（决策 2026-08-08 §4）：基于原文另存时指向被审文档；
   * 起草工作台右栏据此回捞该文档的审查发现；普通起草为 null */
  sourceDocumentId: string | null;
  /** 「AI 初稿未经人工审阅」护栏标记（2026-08-27 流式写入设计 Q3）：空稿
   * 首轮 AI 直写落库时置位；首次实质人工编辑保存或显式确认审阅后解除。
   * 导出/分享按本标记触发与「存在待决提案」同强度的二次确认。 */
  aiDraftUnreviewed?: boolean;
  createdAt: string;
  updatedAt: string;
}

/** POST /draft-docs body. */
export interface DraftDocCreateRequest {
  /** 缺省时服务端置「未命名草稿」 */
  title?: string | null;
  matterId?: string | null;
  docType?: DraftDocType | null;
}

/**
 * PATCH /draft-docs/{id} body.
 *
 * `matterId` 按「字段是否出现」区分语义：未出现 = 不改；显式 null = 解除
 * 事项归属；UUID = 绑定（要求目标事项成员身份）。调用方须只在确需改动时
 * 携带该字段。
 */
export interface DraftDocUpdateRequest {
  title?: string;
  matterId?: string | null;
  docType?: DraftDocType;
  status?: DraftDocStatus;
  /** 显式确认审阅：仅接受 false（解除「AI 初稿未经人工审阅」标记）。 */
  aiDraftUnreviewed?: false;
}

/**
 * PUT /draft-docs/{id}/body body — 人工编辑保存（允许空串）。
 *
 * `anchors` 可选（D7，阶段二）：随正文一并上报的锚点数组，与编辑器当前
 * 块序列一一对应（DraftRichEditorHandle.getAnchors() 现算，保存执行时从
 * 编辑器直接取值，与正文同源）。省略该字段时服务端按既有语义处理——正文
 * 变化则清空既有锚点、未变则保留（后端 DraftDocBodyUpdate 文档）。
 */
export interface DraftDocBodyUpdateRequest {
  body: string;
  anchors?: DraftDocAnchor[];
}

/** PUT /draft-docs/{id}/body 响应——`anchorsAccepted` 与 `success`/`data`
 * 同级，体现服务端对本次锚点提交的重算校验结果（一致才落库）。 */
export interface DraftDocBodyUpdateResponse {
  success: boolean;
  data: DraftDoc;
  anchorsAccepted: boolean;
}

/**
 * POST /draft-docs/{id}/save-as-document body（阶段 6 另存为事项文档）。
 *
 * 正文以服务端 currentBody 为准，请求不携带正文（另存前先落库未保存编辑）。
 * `matterId` 只在无事项草稿另存时使用：先绑定该事项再另存（缺失 409）；
 * 已绑定草稿传入不同事项按冲突 409。
 */
export interface DraftDocSaveAsDocumentRequest {
  documentName: string;
  matterId?: string;
}

/**
 * POST /documents/{documentId}/save-as-draft-doc body（决策 2026-08-08 §4）。
 *
 * 两字段都可省：``title`` 缺省时服务端用「<文档标题>（基于原文）」；
 * ``matterId`` 只在源文档未挂事项（PERSONAL / ORG）时必填。
 */
export interface DocumentSaveAsDraftDocRequest {
  title?: string;
  matterId?: string;
}

/**
 * POST /documents/{documentId}/save-as-draft-doc、POST
 * /knowledge/{knowledgeId}/open-as-draft-doc 响应信封（2026-08-26 表格插入
 * 批次，评估文档轴四）。
 *
 * `sourceHasTables`：源文档（或文件型范本）是 .docx 且检测出含表格时为
 * `true`——引擎提取阶段表格内容已整体丢失（`extracted_text` 里找不到任何
 * 痕迹），`currentBody` 不会包含源文档的表格内容。调用方据此在打开新草稿
 * 时弹一次性告警，把已知的静默损坏变成用户知情的损坏。不是 `DraftDoc`
 * 实体本身的持久字段——只是这次调用的一次性检测结果，不落库、不随
 * `GET /draft-docs/{id}` 之类的后续查询返回。
 */
export interface DraftDocOpenResponse {
  success: boolean;
  data: DraftDoc;
  sourceHasTables: boolean;
}

/** GET /draft-docs query filters（草稿箱列表）。 */
export interface DraftDocListParams {
  /** 按事项过滤（要求成员身份） */
  matterId?: string;
  /** true = 仅无事项自由起草；与 matterId 同传时以 matterId 为准 */
  unassigned?: boolean;
  docType?: DraftDocType;
  status?: DraftDocStatus;
  /** 标题模糊检索 */
  q?: string;
  page?: number;
  pageSize?: number;
}

/** A per-AI-round body snapshot (jerry_draft_versions；阶段 2 起有数据). */
export interface DraftDocVersion {
  id: string;
  draftId: string;
  roundNo: number;
  body: string;
  messageId: string | null;
  /** 版本来源：baseline=提案首次决策前基线快照（既有语义）；
   * ai_first_draft=空稿首轮 AI 直写的「AI 首稿」里程碑（roundNo 恒 0）。 */
  source?: "baseline" | "ai_first_draft";
  /** 「AI 首稿」版本的触发指令原文（仅 ai_first_draft）。 */
  triggerInstruction?: string | null;
  /** 操作人（发起首轮指令的律师）；baseline 行为 null。 */
  createdBy?: string | null;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// 起草会话流式契约 — POST /draft-docs/{id}/messages 的 SSE 事件模型。
// 镜像后端 DraftStreamEvent / DraftDocChangeType / DraftStreamDoneStatus
// (app/modules/jerry/enums.py)；契约文档 draft-workbench-design.md §6.2。
// ---------------------------------------------------------------------------

/** POST /draft-docs/{id}/messages 请求体。 */
export interface DraftDocMessageRequest {
  content: string;
  /** 本轮参考资料（jerry 文档 id）；非 READY 的服务端静默排除 */
  referenceDocumentIds?: string[];
  /** 1A-4：本轮参考的律所范本与标准（资源中心条目 id）；服务端按可见性与
   * 索引状态过滤后与事项文档合并进同一检索范围 */
  referenceResourceIds?: string[];
  /** 批次 5：本轮所属起草会话——首次发送前必先建会话（create-on-first-send） */
  conversationId: string;
  /** 与聊天通道同义（enabledTools 如 web-search；dataSources 为外部数据源 key）。
   * 后端 DraftDocMessageCreate 已接受两者；起草输入侧的工具开关接入前恒缺省。 */
  enabledTools?: string[];
  dataSources?: string[];
}

/** SSE 事件名（`event:` 行的取值）。 */
export const DRAFT_STREAM_EVENT = {
  MESSAGE_DELTA: "message.delta",
  DOCUMENT_CHANGE: "document.change",
  DOCUMENT_PROPOSALS: "document.proposals",
  DOCUMENT_SNAPSHOT: "document.snapshot",
  CITATION: "citation",
  ERROR: "error",
  DONE: "done",
} as const;

export type DraftStreamEventType =
  (typeof DRAFT_STREAM_EVENT)[keyof typeof DRAFT_STREAM_EVENT];

/** 改动类型：edit = 引擎 Edit 工具定点替换；rewrite = Write 工具整篇重写。 */
export type DraftDocChangeType = "edit" | "rewrite";

export const DRAFT_DOC_CHANGE_TYPE_LABELS: Record<DraftDocChangeType, string> = {
  edit: "定点修改",
  rewrite: "整篇重写",
};

/** `document.change` 载荷——单条草稿改动（右栏改动卡片）。 */
export interface DraftDocChange {
  changeId: string;
  changeType: DraftDocChangeType;
  /** rewrite 时为 null（无定点原文） */
  before: string | null;
  after: string;
}

/** `document.delta` 载荷——首轮流式写入的正文增量帧（2026-08-27 设计，
 * 引擎 ADR §4.3.1 经 jerry 转译，camelCase）。增量帧 final=false；终止帧
 * final=true、delta 为空串、filePath 为权威值。matchesDraft 为 jerry 派生
 * 三态：true=命中本草稿工作区文件；false=指向其它文件（整体丢弃该调用的
 * 缓冲，不弹取消提示）；null=路径尚未解析出（缓冲不渲染）。 */
export interface DraftDocumentDelta {
  modelToolCallId: string;
  name: string;
  field: string | null;
  filePath: string | null;
  delta: string;
  final: boolean;
  matchesDraft: boolean | null;
}

/** `document.snapshot` 载荷——本轮结束后的正文全量与版本号。 */
export interface DraftDocSnapshot {
  body: string;
  /** 本轮未产生改动（changed=false）或快照保存失败时为 null */
  roundNo: number | null;
  changed: boolean;
  /** D1 阶段三（ADR 2026-08-16 方案 C）：草稿当前待决提案总数（跨全部
   * 批次），驱动编辑器区顶部常驻计数条。字段设为可选（而非必填）以兼容
   * 锚点上线前的既有测试夹具；缺省按 0 处理。 */
  pendingProposalCount?: number;
  /** 首轮直写例外（2026-08-27 方案甲）：true = 本轮正文已直写落库（body 即
   * 新正文）且「AI 首稿」里程碑已固化——前端以此为轮次级生效信号。可选以
   * 兼容既有夹具，缺省按 false 处理。 */
  firstDraftPersisted?: boolean;
}

// ---------------------------------------------------------------------------
// D1 阶段三 — 提案层（ADR 2026-08-16《AI 改动提案层设计》方案 C）
// 镜像后端 DraftProposalKind / DraftProposalDecision / DraftProposalBatchStatus
// (app/modules/jerry/enums.py)；DraftProposalResponse / DraftProposalBatchResponse
// (app/modules/jerry/schemas.py) 序列化 camelCase。
// ---------------------------------------------------------------------------

/** 提案种类：replace/insert/delete 由块级差异算法直接产出；replace_all 是
 * 提案数或改动块占比超阈值时的整批降级形态（整篇对照，不定位单一块）。 */
export type DraftProposalKind = "replace" | "insert" | "delete" | "replace_all";

/** 提案种类中文标签。 */
export const DRAFT_PROPOSAL_KIND_LABELS: Record<DraftProposalKind, string> = {
  replace: "替换",
  insert: "插入",
  delete: "删除",
  replace_all: "整篇替换",
};

/** 单条提案的决策状态。一旦离开 pending 即不可逆（重复提交返回 409）。 */
export type DraftProposalDecision = "pending" | "accepted" | "rejected";

/** 提案批次状态：pending=批内尚有待决提案；decided=已全部决策完毕；
 * superseded 为数据模型预留值，当前阶段实现不产生该状态。 */
export type DraftProposalBatchStatus = "pending" | "decided" | "superseded";

/**
 * 批次内的单条提案（`GET /draft-docs/{id}/proposals` 与 SSE
 * `document.proposals` 帧携带）。`anchorId`/`anchorOrdinal` 在
 * `kind==="replace_all"` 时为 null（整篇替换不定位单一块）。`rangeStart`/
 * `rangeEnd` 是该提案覆盖的基线块半开区间 `[rangeStart, rangeEnd)`——采纳
 * insert/delete 提案时用于按锚点节点位置做对应编辑，见
 * `components/draft/editor/draftProposalAnchor.ts`。`note` 当前始终为
 * null（批次级说明改由 `DraftProposalBatch.assistantReplySummary` 承载），
 * 保留为未来逐条理由的占位。
 */
export interface DraftProposal {
  id: string;
  batchId: string;
  seqNo: number;
  kind: DraftProposalKind;
  anchorId: string | null;
  anchorOrdinal: number | null;
  rangeStart: number;
  rangeEnd: number;
  originalText: string | null;
  suggestedText: string | null;
  note: string | null;
  decision: DraftProposalDecision;
  decidedBy: string | null;
  decidedAt: string | null;
  isBatchDecision: boolean;
  createdAt: string;
}

/**
 * 一轮 AI 修改产出的提案批次。`baselineBody`/`resultBody` 支撑「全部采纳」
 * 直接覆盖正文（不必逐条重放）；`baselineAnchors` 为生成本批次时的基线
 * 锚点快照，与 `rangeStart`/`rangeEnd` 配合可判断 insert 提案是「插入到
 * 某块之前」还是「插入到基线末块之后」（`rangeStart === baselineAnchors.length`
 * 时为后者）。`proposals` 在 `GET /draft-docs/{id}/proposals` 响应中随批次
 * 一并携带；`PATCH`/`decide-all` 端点响应视调用方需要另行附加。
 */
export interface DraftProposalBatch {
  id: string;
  draftId: string;
  roundNo: number;
  baselineBody: string;
  resultBody: string;
  baselineAnchors: DraftDocAnchor[];
  assistantReplySummary: string | null;
  status: DraftProposalBatchStatus;
  createdAt: string;
  proposals: DraftProposal[];
}

/** PATCH /draft-docs/{id}/proposals/{proposalId} body——单条提案决策。 */
export interface DraftProposalDecisionRequest {
  decision: "accepted" | "rejected";
}

/** POST /draft-docs/{id}/proposal-batches/{batchId}/decide-all body——整批
 * 采纳或整批拒绝。 */
export interface DraftProposalBatchDecisionRequest {
  decision: "accepted" | "rejected";
}

/**
 * `document.proposals` SSE 帧载荷——轮末单帧下发本轮产出的提案批次（方案
 * C）。`batch` 是精简形状（不含 `baselineAnchors`/`proposals` 嵌套，服务层
 * 手工构造，与 `GET` 端点的 {@link DraftProposalBatch} 不同源，见后端
 * `_proposal_batch_sse_payload`）——本帧只用于驱动提案 tab 自动激活与列表
 * 刷新信号，渲染数据以随后 `GET` 提案列表回填的完整批次为准（含
 * baselineAnchors，insert 提案的定位换算需要它）。
 */
export interface DraftProposalsSseFrame {
  batch: {
    id: string;
    draftId: string;
    roundNo: number;
    baselineBody: string;
    resultBody: string;
    assistantReplySummary: string | null;
    status: DraftProposalBatchStatus;
  };
  proposals: DraftProposal[];
}

/**
 * `citation` 载荷——引擎逐条转发的检索来源，字段为引擎侧 snake_case 原样
 * （与 DraftCitation / 会话 Citation 同源），此处按可选字段宽松消费。
 */
export interface DraftStreamCitation {
  /** 引擎给该片段编的 `[#N]` 编号；正文标记与清单条目靠它对应，不靠数组下标。 */
  block_index?: number;
  doc_id?: string;
  doc_title?: string | null;
  title?: string | null;
  section?: string | null;
  page?: number | null;
  snippet?: string | null;
  [key: string]: unknown;
}

/** `error` 载荷——本轮失败（指令保留、可重试）。 */
export interface DraftStreamError {
  code: string;
  message: string;
}

/** `done` 载荷——本轮终态（解锁输入框）。 */
export interface DraftStreamDone {
  status: "ok" | "failed";
  /**
   * G7（迭代 28）：本轮被提前截断时给出的可读说明。
   *
   * 起草通道此前不接引擎的 `truncated_by`，于是一份**半截的**文书在界面上与写完的
   * 文书完全一样。这比聊天里答半截更直接：文书是要落到客户手里的。
   *
   * 未截断时字段缺省（后端对说不清依据的原因一律不给提示，宁缺勿滥）。
   */
  incomplete?: { reason: string | null; message: string };
}

/** 一次完整解析后的 SSE 事件（事件名 + 已 JSON.parse 的载荷）。 */
export type DraftStreamEvent =
  | { type: "message.delta"; data: { content: string } }
  | { type: "document.change"; data: DraftDocChange }
  // 首轮流式写入（2026-08-27）：Write/Edit 参数流增量；仅空稿首轮整篇写入
  // 场景消费（严格空稿门禁），其余场景消费侧静默丢弃。
  | { type: "document.delta"; data: DraftDocumentDelta }
  // D1 阶段三：轮末单帧下发本轮产出的提案批次（方案 C），changed 且非空差异
  // 时才有此帧；document.snapshot 紧随其后下发。
  | { type: "document.proposals"; data: DraftProposalsSseFrame }
  | { type: "document.snapshot"; data: DraftDocSnapshot }
  | { type: "citation"; data: DraftStreamCitation }
  // Step3：工具调用过程态（与 chat 同形状），供前端累积 toolCalls 过程条。
  | {
      type: "tool.use";
      data: {
        toolName: string | null;
        toolLabel: string | null;
        args: Record<string, unknown>;
        toolCallId: string | null;
      };
    }
  | {
      type: "tool.result";
      data: {
        toolCallId: string | null;
        resultCount: number | null;
        toolName: string | null;
        toolLabel: string | null;
        ok: boolean;
        /** 批次二（1.2）：命中文档名，见 `ChatToolResultFrame.hitDocuments`。 */
        hitDocuments?: string[];
        /** 首轮流式写入（2026-08-27）：模型侧调用标识，join document.delta
         * 参数流与执行结果（可能缺失/空串——按「无匹配结果帧」兜底回滚）。 */
        modelToolCallId?: string | null;
      };
    }
  // Step3：本轮开始时下发检索范围（单帧；无参考资料时缺省）。
  | { type: "retrieval.scope"; data: RetrievalScope }
  // 批次三（推理过程时间线就绪层）：与 chat 同形状，见
  // `ChatReasoningDeltaFrame`；与该通道 toolCalls/retrievalScope 同口径，
  // 均不落库（持久化载体 metadata.draftRound 本身存在，只是不携带这三项），
  // 仅供会话内累积展示。
  | { type: "reasoning.delta"; data: { content: string } }
  | { type: "error"; data: DraftStreamError }
  | { type: "done"; data: DraftStreamDone };
